import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Alert, Spinner } from "react-bootstrap";
import HeadingBanner from "../components/HeadingBanner";
import LeftPanel from "../components/LeftPanel";
import OrientationPrompt from "../components/OrientationPrompt";
import CreateSchoolGroupModal from "../components/CreateSchoolGroupModal";
import EditSchoolGroupModal from "../components/EditSchoolGroupModal";
import ManageGroupSchoolsModal from "../components/ManageGroupSchoolsModal";

import "../components/SchoolAdmin.css";
import { FaPlus, FaEdit, FaTrash, FaSchool } from "react-icons/fa";

import {
  whoami,
  getSchoolGroups,
  createSchoolGroup,
  updateSchoolGroup,
  deleteSchoolGroup,
} from "../services/APIClient";

function SchoolGroupAdmin() {
  const [teacherid, setTeacherid] = useState(null);
  const [name, setName] = useState("John Doe");
  const [isPortrait, setIsPortrait] = useState(
    window.matchMedia("(orientation: portrait)").matches
  );
  const [schoolGroups, setSchoolGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const [showModal, setShowModal] = useState({
    createGroup: false,
    editGroup: false,
    manageSchools: false,
  });

  const [selectedGroup, setSelectedGroup] = useState(null);

  useEffect(() => {
    whoami().then((response) => {
      setTeacherid(response.data.stakeholder.id);
      setName(response.data.stakeholder.name);
    });
  }, []);

  useEffect(() => {
    fetchSchoolGroups();
  }, []);

  const fetchSchoolGroups = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getSchoolGroups();
      setSchoolGroups(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching school groups:", err);
      setError(`Failed to load school groups: ${err.message}`);
      setSchoolGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (modalName) => {
    setShowModal({ ...showModal, [modalName]: true });
  };

  const handleCloseModal = (modalName) => {
    setShowModal({ ...showModal, [modalName]: false });
    setSelectedGroup(null);
  };

  const handleCreate = async (groupData) => {
    try {
      setLoading(true);
      await createSchoolGroup({ ...groupData, created_by: name });
      setSuccessMessage("School group created successfully");
      await fetchSchoolGroups();
      handleCloseModal("createGroup");
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error("Error creating school group:", err);
      setError(`Failed to create school group: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (groupData) => {
    try {
      setLoading(true);
      await updateSchoolGroup(selectedGroup.id, groupData);
      setSuccessMessage("School group updated successfully");
      await fetchSchoolGroups();
      handleCloseModal("editGroup");
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error("Error updating school group:", err);
      setError(`Failed to update school group: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (groupId) => {
    if (window.confirm("Are you sure you want to delete this school group? This will not delete the schools themselves.")) {
      try {
        setLoading(true);
        await deleteSchoolGroup(groupId);
        setSuccessMessage("School group deleted successfully");
        await fetchSchoolGroups();
        setTimeout(() => setSuccessMessage(null), 3000);
      } catch (err) {
        console.error("Error deleting school group:", err);
        setError(`Failed to delete school group: ${err.message}`);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditClick = (group) => {
    setSelectedGroup(group);
    handleOpenModal("editGroup");
  };

  const handleManageSchoolsClick = (group) => {
    setSelectedGroup(group);
    handleOpenModal("manageSchools");
  };

  useEffect(() => {
    const handleOrientationChange = () => {
      setIsPortrait(window.innerWidth < window.innerHeight);
    };
    window.addEventListener("resize", handleOrientationChange);
    return () => window.removeEventListener("resize", handleOrientationChange);
  }, []);

  return (
    <div>
      <Container fluid className="class-admin-container">
        <HeadingBanner
          name={name}
          isLoggedIn={true}
          cloud={true}
          teacherid={teacherid}
        />

        <Row className="class-admin-row">
          <LeftPanel />
          <Col sm={{ span: 10, offset: 2 }} className="class-admin-content">

            <div className="class-admin-header">
              <h2 className="class-admin-page-title">School Group Admin</h2>
              <p className="class-admin-page-subtitle">
                Create and manage groups of schools.
              </p>
            </div>

            {successMessage && (
              <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)} className="mb-4">
                {successMessage}
              </Alert>
            )}

            {error && (
              <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-4">
                {error}
              </Alert>
            )}

            <div className="mb-4">
              <Button
                variant="primary"
                size="lg"
                onClick={() => handleOpenModal("createGroup")}
                className="d-flex align-items-center"
              >
                <FaPlus className="me-2" />
                Create New Group
              </Button>
            </div>

            {loading && !schoolGroups.length ? (
              <div className="text-center py-5">
                <Spinner animation="border" role="status" className="mb-3">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p>Loading school groups...</p>
              </div>
            ) : schoolGroups.length === 0 ? (
              <Alert variant="info">
                No school groups exist yet. Create your first group to get started.
              </Alert>
            ) : (
              <div className="tags-grid">
                {schoolGroups.map((group) => (
                  <Card key={group.id} className="tag-definition-card mb-3">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="flex-grow-1">
                          <h5 className="mb-1">{group.name}</h5>
                        </div>
                        <div className="d-flex gap-2 ms-3">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => handleManageSchoolsClick(group)}
                          >
                            <FaSchool className="me-1" />
                            Manage Schools
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleEditClick(group)}
                          >
                            <FaEdit className="me-1" />
                            Edit
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(group.id)}
                          >
                            <FaTrash />
                          </Button>
                        </div>
                      </div>
                      {group.created_by && (
                        <div className="text-muted small mt-2 pt-2 border-top">
                          Created by {group.created_by}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                ))}
              </div>
            )}

          </Col>
        </Row>
      </Container>

      {isPortrait && <OrientationPrompt />}

      <CreateSchoolGroupModal
        show={showModal.createGroup}
        onHide={() => handleCloseModal("createGroup")}
        onSubmit={handleCreate}
        loading={loading}
      />

      {selectedGroup && showModal.editGroup && (
        <EditSchoolGroupModal
          show={showModal.editGroup}
          onHide={() => handleCloseModal("editGroup")}
          group={selectedGroup}
          onSubmit={handleEdit}
          loading={loading}
        />
      )}

      {selectedGroup && showModal.manageSchools && (
        <ManageGroupSchoolsModal
          show={showModal.manageSchools}
          onHide={() => handleCloseModal("manageSchools")}
          group={selectedGroup}
          onMembershipChanged={fetchSchoolGroups}
        />
      )}
    </div>
  );
}

export default SchoolGroupAdmin;
